import {forwardRef} from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import classNames from 'classnames';
import {t} from 'i18next';

import ChevronRight from 'assets/icons/chevronRight';
import {ThinChevronRight} from 'assets/icons/thinChevronRight';
import ThumbsUpIcon from 'assets/icons/thumbsUpIcon';
import theme from 'config/theme';
import {isPDPRevamp} from 'constants/recommendation';
import {useElementVisibleInViewport} from 'hooks/useElementVisibleInViewport';
import ReactNative from 'services/react-native';

import {
  CarouselListContent,
  CarouselListInnerWrapper,
  CarouselListTitleContainer,
  CarouselListWrapperContainer,
  CarouselListWrapperContent,
  SeeAllText,
  SeeAllTextContainer,
  TitleIconHolder,
  TitleText
} from './styles';
import {CarouselListContainerProps} from './types';
import {HorizontalScroller} from '../horizontal-scroller';

export const CarouselListContainer = forwardRef<Element, CarouselListContainerProps>((props, visibleElementRef) => {
  const {
    title,
    children,
    isShowSeeAll,
    handleOnCarouselLeftClick,
    handleOnCarouselRightClick,
    handleOnSeeAllClick,
    triggerCarouselScrollAnalytics,
    isSearchBoxCarousel,
    showShadowArrow,
    hideTopSeeAll = false,
    isFullHeight = false
  } = props;
  const {setNodeRef} = useElementVisibleInViewport({threshold: 1});
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));

  const SeeAllIcon = isPDPRevamp ? ThinChevronRight : ChevronRight;

  const handleOnLeftClick = () => {
    handleOnCarouselLeftClick && handleOnCarouselLeftClick();
  };

  const handleOnRightClick = () => {
    handleOnCarouselRightClick && handleOnCarouselRightClick();
  };

  return (
    <CarouselListWrapperContainer
      maxWidth={'lg'}
      className={classNames({
        removePadding: false,
        isSearchBoxCarousel: isSearchBoxCarousel,
        removeMargin: isFullHeight
      })}
      isReactNativeView={isReactNativeView || isMobileView}
    >
      <CarouselListInnerWrapper>
        {title && (
          <CarouselListTitleContainer>
            {isSearchBoxCarousel && (
              <TitleIconHolder>
                <ThumbsUpIcon fontSize="small" />
              </TitleIconHolder>
            )}
            <TitleText
              data-test-id={`${title}_lbl_carousel_name`}
              className={classNames({searchBoxCarousel: isSearchBoxCarousel})}
            >
              {title}
            </TitleText>
            {isShowSeeAll && !hideTopSeeAll && (
              <SeeAllTextContainer onClick={() => handleOnSeeAllClick('seeAll')}>
                <SeeAllText data-test-id={'lbl_see_all'} className={classNames({revampInPDPpage: isPDPRevamp})}>
                  {t('global.seeAll')}
                </SeeAllText>
                <SeeAllIcon width={16} height={16} />
              </SeeAllTextContainer>
            )}
          </CarouselListTitleContainer>
        )}
        <CarouselListContent ref={setNodeRef}>
          <CarouselListWrapperContent ref={visibleElementRef}>
            <HorizontalScroller
              isShowSeeAll={isShowSeeAll}
              onClickLeftScrollButton={handleOnLeftClick}
              onClickRightScrollButton={handleOnRightClick}
              showShadow={isPDPRevamp && showShadowArrow}
              scrollButtonAlignmentType={undefined}
              onSeeAllClickHandler={() => handleOnSeeAllClick('seeAllCard')}
              triggerCarouselScrollAnalytics={triggerCarouselScrollAnalytics}
            >
              {children}
            </HorizontalScroller>
          </CarouselListWrapperContent>
        </CarouselListContent>
      </CarouselListInnerWrapper>
    </CarouselListWrapperContainer>
  );
});

CarouselListContainer.displayName = 'CarouselListContainer';
